import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

function mapResetPasswordError(err) {
  if (!err) return null
  if (err.code === 'weak_password') {
    const reasons = err.weakPassword?.reasons ?? []
    if (reasons.includes('pwned')) {
      return 'This password has appeared in a known data breach. Please choose a different one.'
    }
    if (reasons.includes('characters')) {
      return 'Password must include a mix of character types (lowercase, uppercase, digits, symbols).'
    }
    if (reasons.includes('length')) {
      return 'Password is too short. Please use a longer password.'
    }
    return 'Password is too weak. Try a longer or more varied password.'
  }
  if (err.code === 'same_password') {
    return 'New password must differ from your current password.'
  }
  if (err.code === 'over_request_rate_limit' || err.status === 429) {
    return 'Too many requests. Please try again in a few minutes.'
  }
  return null
}

export default function ResetPasswordPage() {
  const navigate = useNavigate()

  const [password, setPassword]   = useState('')
  const [confirm, setConfirm]     = useState('')
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState('')
  const [ready, setReady]         = useState(false)
  const [checking, setChecking]   = useState(true)
  const [done, setDone]           = useState(false)

  // Supabase fires PASSWORD_RECOVERY when the user arrives via the reset link.
  // We wait up to 2s before concluding the link is invalid.
  useEffect(() => {
    const timer = setTimeout(() => setChecking(false), 2000)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        clearTimeout(timer)
        setReady(true)
        setChecking(false)
      }
    })
    return () => { clearTimeout(timer); subscription.unsubscribe() }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!password) return setError('Please enter a new password.')
    if (password.length < 8) return setError('Password must be at least 8 characters.')
    if (password !== confirm) return setError('Passwords do not match.')
    setLoading(true)
    setError('')
    const { error: err } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (err) {
      setError(mapResetPasswordError(err) ?? err.message)
    } else {
      setDone(true)
      setTimeout(() => navigate('/login'), 3000)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-panel">
        <div className="auth-panel__blobs">
          <div className="auth-panel__blob auth-panel__blob--1" />
          <div className="auth-panel__blob auth-panel__blob--2" />
          <div className="auth-panel__blob auth-panel__blob--3" />
        </div>
        <div className="auth-panel__content">
          <Link to="/" className="auth-panel__logo">
            <div className="auth-panel__logo-icon">V</div>
            Verse
          </Link>
          <blockquote className="auth-panel__quote">
            "Every great story starts with a single word. Yours is waiting."
          </blockquote>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-wrap">
          {checking ? (
            <div className="auth-form-header">
              <p className="auth-form-subtitle">Verifying your reset link…</p>
            </div>
          ) : done ? (
            <div className="auth-form-header">
              <h1 className="auth-form-title">Password updated</h1>
              <p className="auth-form-subtitle">
                Your password has been changed. Redirecting you to sign in…
              </p>
              <Link to="/login" className="btn btn-primary auth-submit" style={{ marginTop: 24, display: 'block', textAlign: 'center' }}>
                Sign in now
              </Link>
            </div>
          ) : !ready ? (
            <div className="auth-form-header">
              <h1 className="auth-form-title">Invalid link</h1>
              <p className="auth-form-subtitle">
                This link is expired or has already been used.
              </p>
              <Link to="/forgot-password" className="btn btn-primary auth-submit" style={{ marginTop: 24, display: 'block', textAlign: 'center' }}>
                Request a new link
              </Link>
            </div>
          ) : (
            <>
              <div className="auth-form-header">
                <h1 className="auth-form-title">Set new password</h1>
                <p className="auth-form-subtitle">Choose a strong password for your account.</p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit} noValidate>
                {error && <div className="auth-error">{error}</div>}
                <div className="auth-field">
                  <label className="auth-label" htmlFor="password">New password</label>
                  <input
                    id="password"
                    type="password"
                    className="auth-input"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError('') }}
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="confirm">Confirm password</label>
                  <input
                    id="confirm"
                    type="password"
                    className="auth-input"
                    placeholder="Repeat your new password"
                    value={confirm}
                    onChange={e => { setConfirm(e.target.value); setError('') }}
                    autoComplete="new-password"
                  />
                </div>
                <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                  {loading ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
